import React, {Component} from "react"

import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import authentication from "@irati/auth"
import teamService from "@irati/identity/services/teams"
import Button from "@irati/ui/src/Button"
import {Team, User} from "@irati/types"
import Flex from "@irati/ui/src/Flex"
import {removeFromUserList} from "@irati/identity/apollo/query/UsersForBusiness"
import addOrRemoveFromArray from "@irati/utils/addOrRemoveFromArray"
import {officesForTenant} from "@irati/identity/utils/tenant/hasMultipleOffices"

import {AreYouSurePopUp} from "../../../components/AreYouSurePopUp/index"
import EditTeamNamePopUp from "../../../components/Teams/EditTeamNamePopUp/EditTeamNamePopUp"
import AddToTeam from "../../../components/Teams/CreateTeam/CreateTeam"
import ManageUsersBottomBar from "../../../components/Users/ManageUsersBottomBar"

import BulkAddUsers from "../../../components/Teams/BulkAddUsersToTeam"
import TeamUserListRender from "../../../components/Teams/TeamUserList/TeamUserList"
import UserList, {INITIAL_USERLIST_VARIABLES} from "../../../components/UserList"
import TeamAdminHeader from "../../../components/AdminHeader/TeamAdminHeader"
import BackLink from "../../../components/Teams/TeamList/BackLink"

const steps = {
  VIEW_TEAM_USER_LIST: "VIEW_TEAM_USER_LIST",
  ADD_TO_TEAM: "ADD_TO_TEAM",
  BULK_ADD_TO_TEAM: "BULK_ADD_TO_TEAM"
}

interface Props extends InjectIntl {
  team: Team
  goBack: () => void
}

interface State {
  total: number
  isEditTeamModalOpen: boolean
  currentStep: string
  selectedEmployees: User[]
  businessUnits: Team[]
}

class TeamUserList extends Component<Props, State> {
  userList = React.createRef<UserList>()
  adminHeader = React.createRef<TeamAdminHeader>()
  deletePopup = React.createRef<any>()

  state: State = {
    total: 0,
    isEditTeamModalOpen: false,
    currentStep: steps.VIEW_TEAM_USER_LIST,
    selectedEmployees: [],
    businessUnits: []
  }

  componentDidMount() {
    this.getBusinessUnits()
  }

  async getBusinessUnits() {
    const businessUnits = await officesForTenant()
    this.setState({businessUnits})
  }

  openEditTeamModal = () => {
    this.setState({
      isEditTeamModalOpen: true
    })
  }

  hideEditTeamModal = () => {
    this.setState({
      isEditTeamModalOpen: false
    })
  }

  handleHide = () => {
    this.resetUserList()
    this.setState({currentStep: steps.VIEW_TEAM_USER_LIST})
  }

  handleEditSuccess = () => {
    this.setState({isEditTeamModalOpen: false})
  }

  openDeleteTeamPopUp = () => {
    this.deletePopup.current.toggle()
  }

  handleDelete = () => {
    const {team} = this.props
    teamService.delete(authentication.userTenant().uuid, team.uuid)
  }

  resetUserList = () => {
    if (this.userList.current) {
      this.userList.current.setVariables(INITIAL_USERLIST_VARIABLES(this.props.team.uuid))
    }
    if (this.adminHeader.current) {
      this.adminHeader.current.refetch()
    }
  }

  removeUserFromTeam = (variables: any, user: User) => {
    removeFromUserList(variables, user)
    teamService.removeUserFromTeam(this.props.team.uuid, user.uuid).then(() => this.resetUserList())
  }

  handleToggleAll = (employees) => {
    this.setState((state) => {
      const {selectedEmployees} = state
      if (employees.length === selectedEmployees.length) {
        return {selectedEmployees: []}
      }
      return {selectedEmployees: [...employees]}
    })
  }

  handleToggle = (user: User) => {
    this.setState((state) => {
      const {selectedEmployees} = state
      const arr = addOrRemoveFromArray(selectedEmployees, user, (a, b) => a.uuid === b.uuid)

      return {
        selectedEmployees: arr
      }
    })
  }

  handleActivationStatusChange = () => {
    this.setState({selectedEmployees: []})
  }

  renderStep = () => {
    const {currentStep, selectedEmployees, businessUnits} = this.state
    const {team} = this.props
    switch (currentStep) {
      case steps.ADD_TO_TEAM: {
        return <AddToTeam useModal={false} hideBackLink currentTeam={team} onHide={this.handleHide} />
      }
      case steps.BULK_ADD_TO_TEAM: {
        return <BulkAddUsers team={team} onHide={this.handleHide} onSuccess={this.handleEditSuccess} />
      }
      default: {
        return (
          <UserList ref={this.userList} teamId={team.uuid} refetchOnMount>
            {({users, variables}) => (
              <>
                <TeamUserListRender
                  team={team}
                  users={users}
                  businessUnits={businessUnits}
                  selectedEmployees={selectedEmployees}
                  onToggle={this.handleToggle}
                  onToggleAll={() => this.handleToggleAll(users)}
                  removeUserFromTeam={(user: User) => this.removeUserFromTeam(variables, user)}
                />
                <ManageUsersBottomBar
                  variables={variables}
                  selectedEmployees={selectedEmployees}
                  teamId={team.uuid}
                  onSave={this.handleActivationStatusChange}
                />
              </>
            )}
          </UserList>
        )
      }
    }
  }

  handleGoBack = () => {
    const {currentStep} = this.state
    if (currentStep === steps.BULK_ADD_TO_TEAM || currentStep === steps.ADD_TO_TEAM) {
      this.setState({currentStep: steps.VIEW_TEAM_USER_LIST})
    } else {
      this.props.goBack()
    }
  }

  render() {
    const {isEditTeamModalOpen, currentStep} = this.state
    const {team, formatMessage} = this.props

    return (
      <div>
        <BackLink linkProps={{onClick: this.handleGoBack}} />
        {currentStep === steps.VIEW_TEAM_USER_LIST && (
          <>
            <TeamAdminHeader ref={this.adminHeader} team={team} />
            <Flex justifyContent="flex-end" marginTop={2} marginBottom={2} alignItems="center">
              <Button destructive onClick={this.openDeleteTeamPopUp} id="admin_team_button_deleteTeam" marginRight={2}>
                <FormattedMessage id="admin.components.editTeamPopUp.button.deleteTeam" />
              </Button>
              <Button id="admin_team_button_renameTeam" onClick={this.openEditTeamModal} marginRight={2}>
                <FormattedMessage id="admin.containers.teamUserList.nameCountAndButton.editText2" />
              </Button>
              <Button
                primary
                onClick={() => this.setState({currentStep: steps.ADD_TO_TEAM})}
                id="admin_team_button_moveUser"
                marginRight={2}>
                <FormattedMessage id="admin.components.moveUser.button" />
              </Button>
              <Button primary onClick={() => this.setState({currentStep: steps.BULK_ADD_TO_TEAM})}>
                <FormattedMessage id="admin.components.bulkMoveUsers.button" />
              </Button>
            </Flex>
          </>
        )}

        {this.renderStep()}

        <EditTeamNamePopUp show={isEditTeamModalOpen} team={team} onHide={this.hideEditTeamModal} onSuccess={this.handleEditSuccess} />
        <AreYouSurePopUp
          ref={this.deletePopup}
          onSubmit={this.handleDelete}
          text={formatMessage({id: "admin.components.editTeamPopUp.areYouSurePopUp.text"}, {teamname: this.props.team.public_name})}
          buttonText={formatMessage({id: "admin.components.editTeamPopUp.areYouSurePopUp.buttonText"})}
        />
      </div>
    )
  }
}

export default injectIntl(TeamUserList)
